/**
 * Socket Provider - Initializes WebSocket connection at app level
 * Add this provider to your App component to enable real-time updates
 */

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { socketService, SocketEventType } from '../services/socketService';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

interface SocketContextType {
  isConnected: boolean;
  socketId: string | undefined;
  subscribe: (room: string) => void;
  unsubscribe: (room: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
  showConnectionToast?: boolean;
}

export function SocketProvider({ children, showConnectionToast = false }: SocketProviderProps) {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState<string | undefined>();
  /**
   * Lu dans les callbacks, jamais dans les dependances de l'effet : le
   * mettre en dependance faisait retomber l'effet des qu'il changeait —
   * c'est-a-dire juste apres la connexion, puisque c'est elle qui l'ecrit.
   * L'effet se nettoyait alors lui-meme (disconnect) et se relancait
   * (reconnect) une fraction de seconde apres chaque connexion reelle : un
   * aller-retour invisible a l'oeil, mais qui reinitialisait la connexion
   * socket a chaque fois qu'un ecran venait tout juste de s'y abonner.
   */
  const wasConnectedRef = useRef(false);

  useEffect(() => {
    // Only connect when authenticated
    if (!isAuthenticated) {
      socketService.disconnect();
      setIsConnected(false);
      setSocketId(undefined);
      wasConnectedRef.current = false;
      return;
    }

    socketService.connect();

    // Listen for connection events
    const unsubConnect = socketService.on('connected', (data: any) => {
      setIsConnected(true);
      setSocketId(data.socketId);

      // Show toast only on reconnection, not initial connection
      if (showConnectionToast && wasConnectedRef.current) {
        toast.success('Connexion temps réel rétablie', { duration: 2000 });
      }
      wasConnectedRef.current = true;
    });

    const unsubDisconnect = socketService.on('disconnect', () => {
      setIsConnected(false);
      setSocketId(undefined);

      if (showConnectionToast && wasConnectedRef.current) {
        toast.error('Connexion temps réel perdue...', { duration: 3000 });
      }
    });

    const unsubError = socketService.on('connect_error', (data: any) => {
      // Only log if authenticated (avoid noise on login page)
      if (isAuthenticated) {
        console.error('[SocketProvider] Connection error:', data.error);
      }
    });

    return () => {
      unsubConnect();
      unsubDisconnect();
      unsubError();
      socketService.disconnect();
    };
  }, [isAuthenticated, showConnectionToast]);

  // No need to watch localStorage for token changes - cookie is managed by the browser

  const value: SocketContextType = {
    isConnected,
    socketId,
    subscribe: (room: string) => socketService.subscribe(room),
    unsubscribe: (room: string) => socketService.unsubscribe(room),
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocketContext(): SocketContextType {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocketContext must be used within a SocketProvider');
  }
  return context;
}

/**
 * Connection status indicator component
 */
export function ConnectionIndicator() {
  const { isConnected } = useSocketContext();

  return (
    <div className="flex items-center gap-2 text-sm">
      <div
        className={`w-2 h-2 rounded-full ${
          isConnected ? 'bg-green-500' : 'bg-red-500'
        }`}
      />
      <span className="text-gray-500">
        {isConnected ? 'Temps réel' : 'Hors ligne'}
      </span>
    </div>
  );
}

export default SocketProvider;
